import React, { useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus, Table2, BarChart3, Info } from "lucide-react";
import UserAvatar from "../UserAvatar";
import { useTranslation, useFormat } from "../../i18n";
import { INK } from "./palette";

/* ------------------------------------------------------------------------ */
/* Delta badge: "+12% vs previous"                                            */
/* ------------------------------------------------------------------------ */

export const DeltaBadge = ({ metric, invert = false, suffix }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  if (!metric || metric.previous === undefined) return null;
  const pct = metric.delta_pct;
  const delta = metric.delta;
  let tone = "neutral";
  if (delta > 0) tone = invert ? "bad" : "good";
  if (delta < 0) tone = invert ? "good" : "bad";
  const cls =
    tone === "good"
      ? "text-emerald-700 bg-emerald-50"
      : tone === "bad"
        ? "text-rose-700 bg-rose-50"
        : "text-slate-600 bg-slate-100";
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const label =
    pct === null || pct === undefined
      ? metric.previous === 0 && delta > 0
        ? t("delta.new")
        : t("delta.flat")
      : fmt.number(pct / 100, { style: "percent", maximumFractionDigits: 0, signDisplay: "exceptZero" });
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] sm:text-[11px] font-semibold ${cls}`}
      title={t("delta.vs_previous", { value: fmt.number(metric.previous) + (suffix || "") })}
    >
      <Icon className="w-3 h-3" aria-hidden="true" />
      <span className="sr-only">{delta > 0 ? t("delta.up") : delta < 0 ? t("delta.down") : t("delta.flat")}</span>
      {label}
    </span>
  );
};

/* ------------------------------------------------------------------------ */
/* KPI tile                                                                   */
/* ------------------------------------------------------------------------ */

const TILE_TONES = {
  purple: "text-purple-700 bg-purple-50 border-purple-100",
  blue: "text-blue-700 bg-blue-50 border-blue-100",
  green: "text-emerald-700 bg-emerald-50 border-emerald-100",
  yellow: "text-amber-700 bg-amber-50 border-amber-100",
  pink: "text-pink-700 bg-pink-50 border-pink-100",
  teal: "text-teal-700 bg-teal-50 border-teal-100",
  slate: "text-slate-700 bg-slate-50 border-slate-200",
  red: "text-rose-700 bg-rose-50 border-rose-100",
};

export const KpiTile = ({ icon: Icon, label, value, sub, metric, invert, tone = "purple", progress, hint }) => {
  const cls = TILE_TONES[tone] || TILE_TONES.purple;
  return (
    <div className="p-3 sm:p-4 rounded-2xl border border-slate-200 bg-white min-w-0 flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {Icon && (
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg border ${cls} flex-shrink-0`}>
              <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
          )}
          <p className="text-[11px] sm:text-xs font-medium text-slate-500 truncate" title={label}>
            {label}
          </p>
        </div>
        {hint && (
          <span title={hint} className="text-slate-300 hover:text-slate-500 flex-shrink-0">
            <Info className="w-3.5 h-3.5" aria-hidden="true" />
          </span>
        )}
      </div>
      <div className="flex items-end justify-between gap-2">
        <p className="text-xl sm:text-2xl font-bold text-slate-900 truncate leading-none">{value}</p>
        <DeltaBadge metric={metric} invert={invert} />
      </div>
      {progress !== undefined && progress !== null && (
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" aria-hidden="true">
          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
        </div>
      )}
      {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Card wrapper with optional chart/table toggle                              */
/* ------------------------------------------------------------------------ */

export const ChartCard = ({ title, subtitle, icon: Icon, actions, table, children, className = "", bodyClassName = "" }) => {
  const { t } = useTranslation("analytics");
  const [showTable, setShowTable] = useState(false);
  return (
    <section className={`bg-white rounded-2xl border border-slate-200 p-3 sm:p-5 min-w-0 ${className}`}>
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-semibold text-slate-800 text-sm sm:text-base">
            {Icon && <Icon className="w-4 h-4 text-purple-600 flex-shrink-0" aria-hidden="true" />}
            <span className="truncate">{title}</span>
          </h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-1 flex-wrap flex-shrink-0">
          {actions}
          {table && (
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium text-slate-500 hover:bg-slate-100"
              aria-pressed={showTable}
              title={showTable ? t("card.show_chart") : t("card.show_table")}
            >
              {showTable ? <BarChart3 className="w-3.5 h-3.5" /> : <Table2 className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{showTable ? t("card.chart") : t("card.table")}</span>
            </button>
          )}
        </div>
      </header>
      <div className={bodyClassName}>{showTable && table ? <DataTable {...table} compact /> : children}</div>
    </section>
  );
};

/* ------------------------------------------------------------------------ */
/* Segmented control (chart mode, granularity...)                            */
/* ------------------------------------------------------------------------ */

export const Segmented = ({ value, onChange, options, size = "sm", ariaLabel }) => (
  <div className="inline-flex gap-0.5 bg-slate-100 rounded-full p-0.5" role="radiogroup" aria-label={ariaLabel}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        onClick={() => onChange(o.value)}
        className={`px-2.5 ${size === "sm" ? "py-0.5 text-[11px]" : "py-1 text-xs"} rounded-full font-medium whitespace-nowrap transition-colors ${
          value === o.value ? "bg-white text-purple-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/* ------------------------------------------------------------------------ */
/* Empty / loading / error states                                             */
/* ------------------------------------------------------------------------ */

export const EmptyState = ({ message, height = 240 }) => {
  const { t } = useTranslation("analytics");
  return (
    <div className="flex items-center justify-center text-slate-400 text-sm" style={{ height }}>
      {message || t("no_data")}
    </div>
  );
};

export const Skeleton = ({ height = 240 }) => (
  <div className="animate-pulse rounded-xl bg-slate-100" style={{ height }} aria-hidden="true" />
);

export const ErrorState = ({ error, onRetry }) => {
  const { t } = useTranslation("analytics");
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-slate-700 font-medium">{t("failed_title")}</p>
      <p className="text-slate-500 text-sm mt-1">{error?.message || t("load_failed")}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-4 px-4 py-2 rounded-full bg-purple-600 text-white text-sm font-medium hover:bg-purple-700">
          {t("retry")}
        </button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* People                                                                     */
/* ------------------------------------------------------------------------ */

export const PersonCell = ({ user, sub, size = "sm" }) => {
  const name = user?.full_name || user?.email || "";
  const dims = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  return (
    <div className="flex items-center gap-2 min-w-0">
      <UserAvatar src={user?.avatar_url} name={name} className={`${dims} rounded-full object-cover flex-shrink-0`} textClassName="text-[10px]" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
        {(sub || user?.department) && <p className="text-[11px] text-slate-500 truncate">{sub || user.department}</p>}
      </div>
    </div>
  );
};

export const RankList = ({ rows, valueLabel, renderValue, renderExtra, emptyMessage }) => {
  const fmt = useFormat();
  if (!rows || rows.length === 0) return <EmptyState message={emptyMessage} height={140} />;
  return (
    <ol className="divide-y divide-slate-100">
      {rows.map((row, i) => (
        <li key={row.user?.id || i} className="flex items-center gap-3 py-2">
          <span className={`w-6 text-center text-xs font-bold ${row.rank <= 3 ? "text-purple-600" : "text-slate-400"}`}>{row.rank ?? i + 1}</span>
          <div className="flex-1 min-w-0">
            <PersonCell user={row.user} />
          </div>
          {renderExtra && <div className="text-[11px] text-slate-500 flex-shrink-0">{renderExtra(row)}</div>}
          <div className="text-right flex-shrink-0">
            <p className="text-sm font-semibold text-slate-800 tabular-nums">{renderValue ? renderValue(row) : fmt.number(row.value)}</p>
            {valueLabel && <p className="text-[10px] text-slate-400">{valueLabel}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
};

/* ------------------------------------------------------------------------ */
/* Generic sortable table                                                     */
/* ------------------------------------------------------------------------ */

export const DataTable = ({ columns, rows, keyField, compact = false, initialSort, pageSize = 25, emptyMessage }) => {
  const { t } = useTranslation("analytics");
  const [sort, setSort] = useState(initialSort || null);
  const [page, setPage] = useState(0);
  if (!rows || rows.length === 0) return <EmptyState message={emptyMessage} height={120} />;

  const sorted = sort
    ? [...rows].sort((a, b) => {
        const col = columns.find((c) => c.key === sort.key);
        const av = col?.sortValue ? col.sortValue(a) : a[sort.key];
        const bv = col?.sortValue ? col.sortValue(b) : b[sort.key];
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const r = av < bv ? -1 : 1;
        return sort.dir === "asc" ? r : -r;
      })
    : rows;
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const visible = sorted.slice(page * pageSize, page * pageSize + pageSize);

  const toggle = (key) => {
    setPage(0);
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
  };

  return (
    <div className="overflow-x-auto -mx-1">
      <table className={`w-full ${compact ? "text-xs" : "text-sm"}`}>
        <thead>
          <tr className="border-b border-slate-200">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`py-2 px-2 text-[11px] font-semibold text-slate-500 whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"} ${c.sortable !== false ? "cursor-pointer select-none hover:text-slate-800" : ""}`}
                onClick={c.sortable !== false ? () => toggle(c.key) : undefined}
                aria-sort={sort?.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
              >
                {c.label}
                {sort?.key === c.key && <span aria-hidden="true">{sort.dir === "asc" ? " ↑" : " ↓"}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row, i) => (
            <tr key={keyField ? row[keyField] : i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
              {columns.map((c) => (
                <td key={c.key} className={`py-2 px-2 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.className || ""}`}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pages > 1 && (
        <div className="flex items-center justify-between px-2 py-2 text-xs text-slate-500">
          <span>{t("table.page_of", { page: page + 1, pages })}</span>
          <div className="flex gap-1">
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 rounded-full hover:bg-slate-100 disabled:opacity-40">
              {t("table.prev")}
            </button>
            <button type="button" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 rounded-full hover:bg-slate-100 disabled:opacity-40">
              {t("table.next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Inline share bar (for tables)                                              */
/* ------------------------------------------------------------------------ */

export const ShareBar = ({ value, max = 100, color = "#7c3aed", label }) => (
  <div className="flex items-center gap-2 min-w-[90px]">
    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" aria-hidden="true">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, ((value || 0) / max) * 100)}%`, background: color }} />
    </div>
    <span className="text-xs tabular-nums text-slate-700 w-10 text-right">{label}</span>
  </div>
);

export const Legend = ({ items }) => (
  <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600 mt-2">
    {items.map((it) => (
      <li key={it.label} className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: it.color, border: it.dashed ? `1px dashed ${INK.muted}` : "none" }} aria-hidden="true" />
        {it.label}
      </li>
    ))}
  </ul>
);

export const StatRow = ({ label, value, hint }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500 truncate" title={hint}>
      {label}
    </span>
    <span className="text-sm font-semibold text-slate-800 tabular-nums">{value}</span>
  </div>
);
